import { supabase } from '@/lib/supabaseClient';
import { Category, ContentType } from '@/admin/utils/categoryService';
import {
  CACHE_KEYS,
  getCache,
  setCache,
  deleteCache,
  clearCategoriesCache
} from '@/utils/cacheService';

// Thời gian cache mặc định cho từng loại dữ liệu theo tần suất thay đổi
// Categories là dữ liệu thay đổi rất ít, nên TTL cao hơn nhiều
const CATEGORY_CACHE_TTL = {
  // Dữ liệu có tần suất thay đổi cực thấp (24 giờ = 1 ngày)
  VERY_LOW: 24 * 60 * 60 * 1000,
  // Dữ liệu có tần suất thay đổi thấp (6 giờ)
  LOW: 6 * 60 * 60 * 1000,
  // Dữ liệu có tần suất thay đổi trung bình (1 giờ)
  MEDIUM: 60 * 60 * 1000,
};

// Cache keys cho categories
const CATEGORY_CACHE_KEYS = {
  ALL_CATEGORIES: CACHE_KEYS.ALL_CATEGORIES,
  CATEGORIES_BY_TYPE: (contentType: ContentType) => `cache_categories_${contentType}`,
  CATEGORY_BY_ID: (id: string) => `cache_category_id_${id}`,
  CATEGORY_BY_SLUG: (slug: string) => `cache_category_slug_${slug}`,
  LAST_CATEGORY_UPDATE: 'last_category_update',
  CATEGORY_VERSION: 'category_version', // Dùng để đánh version cho categories cache
};

// Hàm này kiểm tra xem cache có cần refresh không dựa trên version
const shouldRefreshCategoryCache = (): boolean => {
  // Dùng localStorage để kiểm tra version của categories
  // Khi admin thay đổi categories, version sẽ được tăng lên
  try {
    const cachedVersion = localStorage.getItem(CATEGORY_CACHE_KEYS.CATEGORY_VERSION);
    const currentVersion = process.env.NEXT_PUBLIC_CATEGORY_VERSION || '1';
    
    // Nếu version khác nhau, cần refresh cache
    return cachedVersion !== currentVersion;
  } catch (error) {
    console.error('Error checking category cache version:', error);
    return false;
  }
};

// Cập nhật version của categories
const updateCategoryVersion = (): void => {
  try {
    const currentVersion = process.env.NEXT_PUBLIC_CATEGORY_VERSION || '1';
    localStorage.setItem(CATEGORY_CACHE_KEYS.CATEGORY_VERSION, currentVersion);
  } catch (error) {
    console.error('Error updating category version:', error);
  }
};

// Lấy tất cả danh mục với cache nâng cao
export async function getAllCategoriesWithCache(contentType?: ContentType): Promise<Category[]> {
  console.log(`Getting categories with enhanced cache for content type: ${contentType || 'all'}`);
  
  // Tạo cache key dựa trên content type
  const cacheKey = contentType 
    ? CATEGORY_CACHE_KEYS.CATEGORIES_BY_TYPE(contentType) 
    : CATEGORY_CACHE_KEYS.ALL_CATEGORIES;
  
  // Kiểm tra xem có refresh cache không dựa trên version
  const shouldRefresh = shouldRefreshCategoryCache();
  
  // Lấy từ cache nếu có và không cần refresh
  if (!shouldRefresh) {
    const cachedCategories = getCache<Category[]>(cacheKey);
    if (cachedCategories) {
      console.log('Using categories from enhanced cache');
      return cachedCategories;
    }
  } else {
    console.log('Category version changed, refreshing cache');
    // Xóa tất cả cache liên quan đến categories
    clearCategoriesCache();
  }
  
  // Không có trong cache hoặc cần refresh, lấy từ database
  console.log('Fetching categories from database directly');
  try {
    let query = supabase
      .from('categories')
      .select('*');
    
    // Nếu có content_type, lọc theo content_type
    if (contentType) {
      query = query.or(`content_type.eq.${contentType},content_type.eq.${ContentType.ALL}`);
    }
    
    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }

    // Kiểm tra xem có data trả về không
    if (!data || data.length === 0) {
      console.warn('No categories found');
      return [];
    }

    // Lưu kết quả vào cache với TTL cao hơn vì categories thay đổi rất ít
    setCache(cacheKey, data, CATEGORY_CACHE_TTL.VERY_LOW);
    
    // Lưu dữ liệu vào localStorage để có thể truy cập ngoại tuyến
    try {
      localStorage.setItem(cacheKey, JSON.stringify({
        data: data,
        timestamp: Date.now(),
        expiry: Date.now() + CATEGORY_CACHE_TTL.VERY_LOW
      }));
    } catch (e) {
      console.warn('Failed to save categories to localStorage:', e);
    }
    
    // Cập nhật version
    updateCategoryVersion();

    return data as Category[];
  } catch (error) {
    console.error('Error in getAllCategoriesWithCache:', error);
    
    // Thử lấy từ localStorage nếu có lỗi khi fetch từ server
    try {
      const localData = localStorage.getItem(cacheKey);
      if (localData) {
        const parsed = JSON.parse(localData);
        if (parsed && parsed.data && Array.isArray(parsed.data)) {
          console.log('Using categories from localStorage fallback');
          return parsed.data as Category[];
        }
      }
    } catch (e) {
      console.error('Error retrieving from localStorage:', e);
    }
    
    return [];
  }
}

// Lấy danh mục theo ID với cache nâng cao
export async function getCategoryByIdWithCache(id: string): Promise<Category | null> {
  if (!id) return null;
  
  console.log(`Getting category with enhanced cache for id: ${id}`);
  
  const cacheKey = CATEGORY_CACHE_KEYS.CATEGORY_BY_ID(id);
  
  // Kiểm tra xem có refresh cache không dựa trên version
  const shouldRefresh = shouldRefreshCategoryCache();
  
  // Lấy từ cache nếu có và không cần refresh
  if (!shouldRefresh) {
    const cachedCategory = getCache<Category>(cacheKey);
    if (cachedCategory) {
      console.log('Using category from enhanced cache');
      return cachedCategory;
    }
  }
  
  // Không có trong cache hoặc cần refresh, lấy từ database
  try {
    // Thay đổi từ single() sang maybeSingle() để tránh lỗi 406 Not Acceptable
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error(`Error fetching category with id ${id}:`, error);
      throw error;
    }

    if (!data) {
      return null;
    }

    // Lưu vào cache với TTL cao
    setCache(cacheKey, data, CATEGORY_CACHE_TTL.VERY_LOW);

    return data as Category;
  } catch (error) {
    console.error(`Error in getCategoryByIdWithCache for id ${id}:`, error);
    return null;
  }
}

// Lấy danh mục theo slug với cache nâng cao
export async function getCategoryBySlugWithCache(slug: string): Promise<Category | null> {
  if (!slug) return null;
  
  console.log(`Getting category with enhanced cache for slug: ${slug}`);
  
  const cacheKey = CATEGORY_CACHE_KEYS.CATEGORY_BY_SLUG(slug);
  
  // Kiểm tra xem có refresh cache không dựa trên version
  const shouldRefresh = shouldRefreshCategoryCache();
  
  // Lấy từ cache nếu có và không cần refresh
  if (!shouldRefresh) {
    const cachedCategory = getCache<Category>(cacheKey);
    if (cachedCategory) {
      console.log('Using category from enhanced cache');
      return cachedCategory;
    }
  }
  
  // Không có trong cache hoặc cần refresh, lấy từ database
  try {
    // Thay đổi từ single() sang maybeSingle() để tránh lỗi 406 Not Acceptable
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();

    if (error) {
      console.error(`Error fetching category with slug ${slug}:`, error);
      throw error;
    }

    if (!data) {
      return null;
    }

    // Lưu vào cache với TTL cao
    setCache(cacheKey, data, CATEGORY_CACHE_TTL.VERY_LOW);

    return data as Category;
  } catch (error) {
    console.error(`Error in getCategoryBySlugWithCache for slug ${slug}:`, error);
    return null;
  }
}

// Làm mới cache categories (thường gọi sau khi admin thay đổi categories)
export function refreshCategoryCache(): void {
  console.log('Refreshing category cache');
  clearCategoriesCache();
  // Cập nhật version để các client khác biết cần refresh cache
  updateCategoryVersion();
}

// Lấy danh mục theo slug hoặc ID với cache nâng cao
export async function getCategoryBySlugOrIdWithCache(slugOrId: string): Promise<Category | null> {
  if (!slugOrId) {
    console.log('getCategoryBySlugOrIdWithCache called with empty slugOrId');
    return null;
  }
  
  console.log(`Attempting to get category by slug or ID: ${slugOrId}`);
  
  try {
    // Thử lấy theo slug trước
    console.log(`First trying to get by slug: ${slugOrId}`);
    const categoryBySlug = await getCategoryBySlugWithCache(slugOrId);
    if (categoryBySlug) {
      console.log(`Successfully found category by slug: ${slugOrId}`);
      return categoryBySlug;
    }
    
    // Nếu không tìm thấy theo slug, thử lấy theo ID
    console.log(`No category found by slug, trying by ID: ${slugOrId}`);
    try {
      const categoryById = await getCategoryByIdWithCache(slugOrId);
      if (categoryById) {
        console.log(`Successfully found category by ID: ${slugOrId}`);
        return categoryById;
      } else {
        console.log(`No category found by ID: ${slugOrId}`);
      }
    } catch (idError) {
      // Có thể slugOrId không phải là UUID hợp lệ, log và trả về null
      console.log(`Error getting category by ID, likely not a valid UUID: ${slugOrId}`);
      console.error(idError);
    }
    
    console.log(`No category found for slug or ID: ${slugOrId}`);
    return null;
  } catch (error) {
    console.error(`Error in getCategoryBySlugOrIdWithCache for ${slugOrId}:`, error);
    return null;
  }
}

// Lấy version hiện tại của categories
export function getCategoryVersion(): string {
  try {
    return localStorage.getItem(CATEGORY_CACHE_KEYS.CATEGORY_VERSION) || '0';
  } catch (error) {
    console.error('Error getting category version:', error);
    return '0';
  }
}

export interface Category {
  id: string;
  name: string;
  color?: string;
  description?: string;
  icon?: string;
}

// Lấy tất cả categories
export const getAllCategories = async (): Promise<Category[]> => {
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .order('name', { ascending: true });

  if (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }

  return data || [];
};

// Lấy category theo ID
export const getCategoryById = async (id: string): Promise<Category | null> => {
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching category:', error);
    throw error;
  }

  return data;
};

// Lấy các khóa học theo category
export const getCoursesByCategory = async (categoryId: string) => {
  const { data, error } = await supabase
    .from('courses')
    .select(`
      *,
      category:categories(id, name, color)
    `)
    .eq('category_id', categoryId)
    .eq('status', 'published')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching courses by category:', error);
    throw error;
  }

  return data || [];
};

export const categoryService = {
  getAllCategories,
  getCategoryById,
  getCoursesByCategory
}; 